import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CategoryToggle from './components/CategoryToggle';
import FullStatsSwitch from 'containers/Mundial2026/WCCountries/components/FullStatsSwitch';
import PlayerStatsCard from '../../../../components/PlayerStatsCard';
import StatsLegend from '../../../../components/StatsLegend';
import { useTranslation } from 'hooks/useTranslation';
import { useJugadoresStatColumnTooltip } from 'containers/Mundial2026/WCMatchDetails/WCJugadores/hooks/useJugadoresStatColumnTooltip';
import {
  STATISTICS_CATEGORIES,
  STATISTICS_CATEGORY_LABELS,
  STATISTICS_CODE_KEYS,
  STATISTICS_LEGEND_KEYS,
  STATISTICS_LEGENDS_BY_CATEGORY,
} from './constants';
import { mapPlayerToCardProps } from './utils';
import styles from './Statistics.module.scss';

const FULL_STATS_LABEL = {
  label: {
    key: 'eventodeportivo_paises_texto_titulo_verestadistica',
    length: 24,
  },
};
const TOGGLE_WIDTH_VAR = '--statistics-toggle-width';
const REDUCED_PLAYER_LIMIT = 5;

const Statistics = ({ statistics, showFullStats = false, onToggleChange }) => {
  const { translationMapper } = useTranslation();
  const { bindHeaderTooltip, headerTooltipPortal } = useJugadoresStatColumnTooltip();
  const categoryTranslations = useMemo(
    () => translationMapper(STATISTICS_CATEGORY_LABELS),
    [translationMapper],
  );
  const legendTranslations = useMemo(
    () => translationMapper(STATISTICS_LEGEND_KEYS),
    [translationMapper],
  );
  const codeTranslations = useMemo(
    () => translationMapper(STATISTICS_CODE_KEYS),
    [translationMapper],
  );
  const fullStatsLabel = useMemo(
    () => translationMapper(FULL_STATS_LABEL).label,
    [translationMapper],
  );

  const [activeCategory, setActiveCategory] = useState(
    STATISTICS_CATEGORIES.OFFENSIVE,
  );
  const headerRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    const headerEl = headerRef.current;
    const toggleEl = toggleRef.current;
    if (!headerEl || !toggleEl || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const width = entry.contentRect.width;
      requestAnimationFrame(() => headerEl.style.setProperty(TOGGLE_WIDTH_VAR, `${width}px`));
    });
    ro.observe(toggleEl);

    return () => {
      ro.disconnect();
      headerEl.style.removeProperty(TOGGLE_WIDTH_VAR);
    };
  }, []);

  const handleCategoryChange = useCallback(category => {
    setActiveCategory(category);
  }, []);

  const categoryOptions = useMemo(
    () =>
      Object.values(STATISTICS_CATEGORIES).map(value => ({
        value,
        label: categoryTranslations[value] || value,
      })),
    [categoryTranslations],
  );

  const legendItems = useMemo(
    () =>
      (STATISTICS_LEGENDS_BY_CATEGORY[activeCategory] || []).map(code => ({
        code: codeTranslations[code] || code,
        label: legendTranslations[code] || code,
      })),
    [activeCategory, codeTranslations, legendTranslations],
  );
  // statistics viene del context useCountries (countries.data.statistics).
  // Las keys del objeto coinciden con los valores de STATISTICS_CATEGORIES.
  const rawPlayers = statistics?.[activeCategory] ?? [];
  const visiblePlayers = showFullStats ? rawPlayers : rawPlayers.slice(0, REDUCED_PLAYER_LIMIT);
  const hasPlayers = rawPlayers.length > 0;

  return (
    <div className={styles['statistics']}>
      <header ref={headerRef} className={styles['statistics__header']}>
        <div ref={toggleRef} className={styles['statistics__category']}>
          <CategoryToggle
            value={activeCategory}
            onChange={handleCategoryChange}
            options={categoryOptions}
          />
        </div>
        <div className={styles['statistics__switch']}>
          <FullStatsSwitch
            checked={showFullStats}
            onChange={onToggleChange}
            label={fullStatsLabel}
          />
        </div>
      </header>

      <div className={styles['statistics__body']}>
        <div className={styles['statistics__grid']}>
          {hasPlayers
            ? visiblePlayers.map((rawPlayer, idx) => {
                const cardProps = mapPlayerToCardProps(rawPlayer);
                return (
                  <PlayerStatsCard
                    key={
                      rawPlayer.playerId ?? `${cardProps.player.name}-${idx}`
                    }
                    player={cardProps.player}
                    columns={cardProps.columns}
                    codeTranslations={legendTranslations}
                    bindHeaderTooltip={bindHeaderTooltip}
                  />
                );
              })
            : statistics && (
                <p className={styles['statistics__empty']}>
                  No hay jugadores disponibles.
                </p>
              )}
        </div>

        <aside className={styles['statistics__legend']}>
          <StatsLegend items={legendItems} />
        </aside>
      </div>
      {headerTooltipPortal}
    </div>
  );
};

export default Statistics;
